"use client";

import { useEffect, useState, useCallback } from "react";
import { createLogger } from "@/lib/logger";
import { authFetch } from "@/lib/authFetch";
import { FiTrendingUp, FiTrendingDown, FiLoader } from "react-icons/fi";
import { formatCompactCurrency } from "@/lib/format";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
} from "recharts";
import { ChartErrorBoundary } from "@/app/components/ChartErrorBoundary";
import AnimatedNumber from "@/app/components/AnimatedNumber";
import { useT } from "@/hooks/useT";

const logger = createLogger("PnLChart");

interface HistoryPoint {
  date: string;
  dailyPnL: number;
  cumulativePnL: number;
  tradeCount: number;
}

interface Stats {
  totalPnL: number;
}

type TimeFilter = "7d" | "30d" | "90d" | "all";

const DAYS_MAP: Record<TimeFilter, number> = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
  "all": 365,
};

function PnLChartContent() {
  const t = useT();
  const [history, setHistory] = useState<HistoryPoint[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<TimeFilter>("all");
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const days = DAYS_MAP[filter];

      // Fetch both history and stats in parallel
      const [historyRes, statsRes] = await Promise.all([
        authFetch(`/api/portfolio/history?days=${days}`),
        authFetch("/api/portfolio/stats"),
      ]);

      if (!historyRes.ok || !statsRes.ok) {
        throw new Error("Failed to fetch data");
      }

      const [historyData, statsData] = await Promise.all([
        historyRes.json(),
        statsRes.json(),
      ]);

      // Process history data
      let historyPoints: HistoryPoint[] = (historyData.history || []).map((h: any) => ({
        date: h.date,
        dailyPnL: h.dailyPnL || 0,
        cumulativePnL: h.cumulativePnL || 0,
        tradeCount: h.tradeCount || 0,
      }));

      // Add starting point at $0 if we have data
      if (historyPoints.length > 0) {
        const firstDate = new Date(historyPoints[0].date);
        firstDate.setDate(firstDate.getDate() - 1);
        const startDate = firstDate.toISOString().split("T")[0];
        historyPoints.unshift({
          date: startDate,
          dailyPnL: 0,
          cumulativePnL: 0,
          tradeCount: 0,
        });

        // Extend line to today with realized PnL
        const today = new Date().toISOString().split("T")[0];
        const lastPoint = historyPoints[historyPoints.length - 1];
        if (lastPoint.date !== today) {
          historyPoints.push({
            date: today,
            dailyPnL: 0,
            cumulativePnL: lastPoint.cumulativePnL,
            tradeCount: 0,
          });
        }
      }

      setHistory(historyPoints);
      setStats({
        totalPnL: statsData.stats?.totalPnL || 0,
      });
    } catch (err) {
      logger.error("Error fetching data", { error: err instanceof Error ? err.message : String(err) });
      setError(t("portfolio.chart.error"));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const totalPnL = stats?.totalPnL || 0;
  const isPositive = totalPnL >= 0;

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: { payload: HistoryPoint }[] }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white dark:bg-[#18181D] p-3 rounded-[5px] shadow-lg border border-gray-200 dark:border-[#1A1A1A]">
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1 font-medium">{formatDate(data.date)}</p>
          <p className={`font-monument text-sm ${data.cumulativePnL >= 0 ? "text-emerald-600 dark:text-[#00FF9D]" : "text-red-600 dark:text-[#FF1744]"}`}>
            {data.cumulativePnL >= 0 ? "+" : ""}{formatCompactCurrency(data.cumulativePnL)}
          </p>
          {data.dailyPnL !== 0 && (
            <p className={`text-[10px] mt-1 ${data.dailyPnL >= 0 ? "text-emerald-500" : "text-red-500"}`}>
              {t("portfolio.chart.day")}: {data.dailyPnL >= 0 ? "+" : ""}{formatCompactCurrency(data.dailyPnL)}
            </p>
          )}
          <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
            {data.tradeCount} {data.tradeCount !== 1 ? t("portfolio.chart.tradesSuffixPlural") : t("portfolio.chart.tradesSuffix")}
          </p>
        </div>
      );
    }
    return null;
  };

  // Skeleton only on initial mount (no stats yet). On subsequent refetches
  // — triggered by changing the time filter — keep the previous UI visible
  // and dim it so the value doesn't flash to skeleton and back. AnimatedNumber
  // animates from prev → next value cleanly.
  if (loading && !stats) {
    return (
      <div className="bg-white dark:bg-[#18181D] rounded-[5px] p-4 border border-gray-200 dark:border-[#1A1A1A] transition-colors">
        {/* Skeleton header — matches loaded layout (label + text-3xl value) to prevent CLS */}
        <div className="mb-4">
          <div className="h-3 w-20 bg-gray-200 dark:bg-[#1A1A1A] rounded animate-pulse mb-1.5" />
          <div className="h-9 w-40 bg-gray-200 dark:bg-[#1A1A1A] rounded animate-pulse" />
        </div>
        <div className="h-[180px] mb-4 bg-gray-100 dark:bg-[#0f0f12] rounded-[5px] flex items-center justify-center">
          <FiLoader className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-[#18181D] rounded-[5px] p-4 border border-gray-200 dark:border-[#1A1A1A] transition-colors">
        <p className="text-sm text-red-500 text-center py-12">{error}</p>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-[#18181D] rounded-[5px] p-4 border border-gray-200 dark:border-[#1A1A1A] transition-opacity duration-200 ${loading ? "opacity-60" : "opacity-100"}`}>
      {/* Header - Total P&L */}
      <div className="mb-4">
        <h4 className="text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wide font-medium mb-1">
          {t("portfolio.chart.totalPnl")}
        </h4>
        <div className="flex items-center gap-2">
          <span className={`font-monument text-3xl tabular-nums ${isPositive ? "text-emerald-600 dark:text-[#00FF9D]" : "text-red-600 dark:text-[#FF1744]"}`}>
            {isPositive ? "+" : "-"}$<AnimatedNumber value={Math.abs(totalPnL)} decimals={2} />
          </span>
          {isPositive ? (
            <FiTrendingUp className="w-5 h-5 text-emerald-600 dark:text-[#00FF9D]" />
          ) : (
            <FiTrendingDown className="w-5 h-5 text-red-600 dark:text-[#FF1744]" />
          )}
        </div>
      </div>

      {/* Chart — delay render until mounted to avoid Recharts -1 dimension warning */}
      {history.length > 1 && mounted ? (
        <div className="h-[180px] mb-4">
          <ResponsiveContainer width="100%" height="100%" minHeight={1} minWidth={1}>
            <AreaChart data={history} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <defs>
                <linearGradient id="pnlGradientPositive" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00FF9D" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#00FF9D" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="pnlGradientNegative" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FF1744" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#FF1744" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tick={{ fontSize: 10, fill: "#6B7280" }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                tickFormatter={(v) => formatCompactCurrency(v)}
                tick={{ fontSize: 10, fill: "#6B7280" }}
                axisLine={false}
                tickLine={false}
                width={50}
              />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={0} stroke="#374151" strokeDasharray="3 3" />
              <Area
                type="monotone"
                dataKey="cumulativePnL"
                stroke={isPositive ? "#00FF9D" : "#FF1744"}
                strokeWidth={2}
                fill={isPositive ? "url(#pnlGradientPositive)" : "url(#pnlGradientNegative)"}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-[180px] mb-4 flex items-center justify-center bg-gray-50 dark:bg-[#0f0f12] rounded-[5px]">
          <p className="text-sm text-gray-500 dark:text-gray-400">{t("portfolio.chart.empty")}</p>
        </div>
      )}

      {/* Time Filter */}
      <div className="flex gap-2 mb-4">
        {(["7d", "30d", "90d", "all"] as TimeFilter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-[5px] text-xs font-medium transition-colors ${
              filter === f
                ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                : "bg-gray-100 dark:bg-[#0f0f12] text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-[#1A1A1A]"
            }`}
          >
            {t(`portfolio.chart.range.${f}`)}
          </button>
        ))}
      </div>

    </div>
  );
}

// Wrap with error boundary for production resilience
export default function PnLChart() {
  const t = useT();
  return (
    <ChartErrorBoundary
      chartName="P&L Chart"
      height="h-[400px]"
      errorLabel={t("chartError.failedToLoad", { chart: t("chartError.pnl") })}
      retryLabel={t("chartError.retry")}
    >
      <PnLChartContent />
    </ChartErrorBoundary>
  );
}
